import { mutate } from 'swr';
import { proxy, useSnapshot } from 'valtio';

import { publicConfig } from '@public-config';

import { Project, ProjectCollaborator } from 'models';
import { ProjectAccessHelpers } from 'helpers/ProjectAccessHelpers';
import { ProjectCollaboratorHelpers } from 'helpers/ProjectCollaboratorHelpers';

import { InternalTrackingService } from 'services/InternalTrackingService';
import { state as currentProjectState } from 'state/current-project';

import { ApiService } from './ApiService';
import { PayloadSDK } from './PayloadSDK';

const state = proxy({
    suggestedCollaboratorEmail: null as string | null,
});

const useProjectCollaboratorState = () => useSnapshot(state);

const ProjectCollaboratorService = {
    list: async (projectId: Project['id']) => {
        const { docs } = await PayloadSDK.find({
            collection: 'projectCollaborators',
            where: {
                project: {
                    equals: projectId,
                },
            },
            sort: 'createdAt',
            depth: 0,
            pagination: false,
        });

        return docs as ProjectCollaborator[];
    },

    invite: async (
        projectId: Project['id'],
        collaborator: {
            email: string;
            permissions: string[];
        },
        redirect: string,
    ) => {
        InternalTrackingService.track('project.inviteCollaborator', {
            collaborator: collaborator.email,
            permissions: collaborator.permissions,
        });

        await ApiService.post(`${publicConfig.urls.api}/projects/${projectId}/collaborators/invite`, {
            project: projectId,
            collaborator,
            redirect,
        });

        await ProjectCollaboratorService.refresh(projectId);
        await mutate(ProjectAccessHelpers.swr.list(projectId));
    },

    edit: async (collaborator: ProjectCollaborator, permissions: ('project.view' | 'project.edit')[]) => {
        InternalTrackingService.track('project.editCollaborator', {
            collaborator: collaborator.user,
            permissions,
        });

        await PayloadSDK.update({
            collection: 'projectCollaborators',
            id: collaborator.id,
            data: { permissions },
        });

        await ProjectCollaboratorService.refresh(collaborator.project);
    },

    delete: async (collaborator: ProjectCollaborator) => {
        InternalTrackingService.track('project.removeCollaborator', {
            collaborator: collaborator.user,
        });

        await PayloadSDK.delete({
            collection: 'projectCollaborators',
            id: collaborator.id,
        });

        await ProjectCollaboratorService.refresh(collaborator.project);
    },

    refresh: async (projectId: Project['id']) => {
        const collaborators = await ProjectCollaboratorService.list(projectId);

        await mutate(ProjectCollaboratorHelpers.swr.list(projectId), collaborators, { revalidate: false });

        syncCurrentProjectCollaboratorIds(projectId, collaborators);
    },

    setSuggestedCollaboratorEmail: (email: typeof state.suggestedCollaboratorEmail) => {
        state.suggestedCollaboratorEmail = email;
    },

    clearSuggestedCollaboratorEmail: () => {
        state.suggestedCollaboratorEmail = null;
    },
};

/** The project itself only carries the collaborator ids of its join field, which nothing refetches after a change. */
const syncCurrentProjectCollaboratorIds = (projectId: Project['id'], collaborators: ProjectCollaborator[]) => {
    const { project } = currentProjectState;

    if (project?.id === projectId && project.collaborators) {
        project.collaborators.users = { docs: collaborators.map((collaborator) => collaborator.id) };
    }
};

export { ProjectCollaboratorService, useProjectCollaboratorState };
